import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const boxVariants = cva(
  // Base styles
  "",
  {
    variants: {
      variant: {
        // Map container variant (relative, full size, border)
        map: "relative h-full w-full rounded-lg border border-neutral-200",
        // Card-like container variant (white bg, rounded, border)
        card: "bg-white rounded-lg border border-neutral-200",
        // Content container variant (minimal border)
        content: "rounded-md border",
        // Panel variant (white bg, padding, border)
        panel: "bg-white rounded-lg p-4 border border-neutral-200",
        // Section variant (white bg, more padding, border)
        section: "bg-white rounded-lg p-6 border border-neutral-200",
        // Plain container (no border, just rounded)
        plain: "rounded-lg",
      },
      size: {
        default: "",
        sm: "p-2",
        md: "p-4",
        lg: "p-6",
      },
      overflow: {
        none: "",
        hidden: "overflow-hidden",
        auto: "overflow-auto",
        scroll: "overflow-scroll",
      },
      rounded: {
        default: "",
        sm: "rounded",
        md: "rounded-md",
        lg: "rounded-lg",
        xl: "rounded-xl",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "plain",
      size: "default",
      overflow: "none",
      rounded: "default",
    },
  }
);

export interface BoxProps
  extends Omit<React.ComponentProps<"div">, "children">,
    VariantProps<typeof boxVariants> {
  children?: React.ReactNode;
}

/**
 * Generic wrapper component for consistent container styling
 * 
 * Provides reusable variants for common container patterns:
 * - `map`: For map containers (relative, full size, border)
 * - `card`: Card-like containers (white bg, rounded, border)
 * - `content`: Minimal content containers (rounded, border)
 * - `panel`: Panel containers (white bg, padding, border)
 * - `section`: Section containers (white bg, more padding, border)
 * - `plain`: Plain rounded container (no border)
 * 
 * Supports ref forwarding for cases where direct DOM access is needed.
 * 
 * @example
 * ```tsx
 * // Map container
 * <Box variant="map">
 *   <MapContainer>...</MapContainer>
 * </Box>
 * 
 * // Card container
 * <Box variant="card" size="md">
 *   <Typography>Content</Typography>
 * </Box>
 * 
 * // Panel with overflow
 * <Box variant="panel" overflow="auto">
 *   <div>Scrollable content</div>
 * </Box>
 * 
 * // With ref forwarding
 * const ref = useRef<HTMLDivElement>(null);
 * <Box ref={ref} variant="plain">Content</Box>
 * ```
 */
export const Box = React.forwardRef<HTMLDivElement, BoxProps>(
  function Box(
    {
      className,
      variant,
      size,
      overflow,
      rounded,
      children,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(boxVariants({ variant, size, overflow, rounded }), className)}
        {...props}
      >
        {children ?? null}
      </div>
    );
  }
);
